/**
 * Modern, attractive authentication component for login and registration
 * Features beautiful UI with animations and modern design
 */

import React, { useState } from 'react'
import { LogIn, UserPlus, Mail, Lock, Sparkles, X, Eye, EyeOff } from 'lucide-react'
import { useLocale } from '../i18n/LocaleContext'

interface AuthProps {
  mode: 'login' | 'register'
  onModeChange: (mode: 'login' | 'register') => void
  onSubmit: (email: string, password: string, tier?: string) => Promise<void>
  onCancel: () => void
  loading: boolean
  error: string | null
}

const Auth: React.FC<AuthProps> = ({
  mode,
  onModeChange,
  onSubmit,
  onCancel,
  loading,
  error
}) => {
  const { t } = useLocale()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [tier, setTier] = useState('hobby')
  const [showPassword, setShowPassword] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  const validateForm = (): boolean => {
    setValidationError(null)
    
    if (!email.trim()) {
      setValidationError(t('auth.validation.emailRequired'))
      return false
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setValidationError(t('auth.validation.emailInvalid'))
      return false
    }
    
    if (!password) {
      setValidationError(t('auth.validation.passwordRequired'))
      return false
    }
    
    if (mode === 'register' && password.length < 6) {
      setValidationError(t('auth.validation.passwordShort'))
      return false
    }
    
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }
    
    await onSubmit(email, password, mode === 'register' ? tier : undefined)
  }

  const switchMode = () => {
    onModeChange(mode === 'login' ? 'register' : 'login')
    setEmail('')
    setPassword('')
    setValidationError(null)
  }

  return (
    <div className="auth-overlay">
      <div className="auth-container">
        {/* Close button */}
        <button
          onClick={onCancel}
          className="auth-close-btn"
          aria-label={t('common.close')}
        >
          <X size={20} />
        </button>

        {/* Decorative background elements */}
        <div className="auth-background">
          <div className="auth-gradient-1"></div>
          <div className="auth-gradient-2"></div>
          <div className="auth-gradient-3"></div>
        </div>

        {/* Main content */}
        <div className="auth-content">
          {/* Header */}
          <div className="auth-header">
            <div className="auth-icon-wrapper">
              {mode === 'login' ? (
                <LogIn className="auth-icon" />
              ) : (
                <UserPlus className="auth-icon" />
              )}
              <Sparkles className="auth-sparkle auth-sparkle-1" />
              <Sparkles className="auth-sparkle auth-sparkle-2" />
            </div>
            <h2 className="auth-title">
              {mode === 'login' ? t('auth.title.login') : t('auth.title.register')}
            </h2>
            <p className="auth-subtitle">
              {mode === 'login'
                ? t('auth.subtitle.login')
                : t('auth.subtitle.register')}
            </p>
          </div>

          {/* Error message */}
          {(error || validationError) && (
            <div className="auth-error">
              <div className="auth-error-icon">⚠️</div>
              <span>{error || validationError}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="auth-form">
            {/* Email field */}
            <div className="auth-field">
              <label htmlFor="auth-email" className="auth-label">
                <Mail size={16} />
                {t('auth.email')}
              </label>
              <input
                id="auth-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.email.placeholder')}
                required
                className="auth-input"
                disabled={loading}
              />
            </div>

            {/* Password field */}
            <div className="auth-field">
              <label htmlFor="auth-password" className="auth-label">
                <Lock size={16} />
                {t('auth.password')}
              </label>
              <div className="auth-password-wrapper">
                <input
                  id="auth-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.password.placeholder')}
                  required
                  className="auth-input"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="auth-password-toggle"
                  disabled={loading}
                  aria-label={showPassword ? t('auth.password.hide') : t('auth.password.show')}
                >
                  {showPassword ? (
                    <EyeOff size={18} />
                  ) : (
                    <Eye size={18} />
                  )}
                </button>
              </div>
            </div>

            {/* Tier selection (register only) */}
            {mode === 'register' && (
              <div className="auth-field">
                <label htmlFor="auth-tier" className="auth-label">
                  {t('auth.tier')}
                </label>
                <select
                  id="auth-tier"
                  value={tier}
                  onChange={(e) => setTier(e.target.value)}
                  className="auth-select"
                  disabled={loading}
                >
                  <option value="hobby">{t('auth.tier.hobby')}</option>
                  <option value="research">{t('auth.tier.research')}</option>
                  <option value="pro">{t('auth.tier.pro')}</option>
                </select>
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading}
              className="auth-submit-btn"
            >
              {loading ? (
                <>
                  <div className="auth-spinner"></div>
                  {mode === 'login' ? t('auth.loading.login') : t('auth.loading.register')}
                </>
              ) : (
                <>
                  {mode === 'login' ? t('auth.submit.login') : t('auth.submit.register')}
                </>
              )}
            </button>
          </form>

          {/* Mode switch */}
          <div className="auth-switch">
            <span className="auth-switch-text">
              {mode === 'login'
                ? t('auth.switch.noAccount')
                : t('auth.switch.hasAccount')}
            </span>
            <button
              type="button"
              onClick={switchMode}
              className="auth-switch-btn"
              disabled={loading}
            >
              {mode === 'login' ? t('auth.switch.signUp') : t('auth.submit.login')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Auth
